import { getIdbStore } from '@/infra/persistence/IdbStore';
import { getLocalStore } from '@/infra/persistence/LocalStore';
import { saveCodec } from '@/infra/persistence/save-codec';
import type { SaveV6 } from '@/infra/persistence/schema-v6';
import { emptySaveV6, CURRENT_SCHEMA_MINOR } from '@/infra/persistence/schema-v6';
import { logger } from '@/infra/logger/Logger';
import { notify } from './notification';
import { getStore, useStore } from '@/infra/store';
import type { Creature } from '@/domain/creature/types';
import { normalizeCareProfile } from '@/domain/care/profile';
import { normalizeWildBestiary } from '@/domain/world/wild-bestiary';
import { recoverWorldPosition } from '@/domain/world/world-position-recovery';
import { regionRegistry } from '@/domain/world/region-registry';
import { footprintForPresentation } from '@/domain/world/actor-presentation';
import { displayHeightForStage } from '@/domain/world/world-scale';

const SAVE_KEY = 'himotchi.save';
const FALLBACK_KEY = 'himotchi.save.fallback';
const FALLBACK_MAX_BYTES = 5_000_000;

export type SaveOutcome =
  | { kind: 'persisted'; store: 'idb' | 'localStorage' }
  | { kind: 'session-only' };

export type LoadOutcome =
  | { kind: 'loaded'; snapshot: SaveV6; from: 'idb' | 'localStorage' }
  | { kind: 'fresh' }
  | { kind: 'corrupted' };

/** Serializa o estado atual da store em SaveV6. */
export function buildSnapshot(now: number = Date.now()): SaveV6 {
  const s = getStore();
  const base = emptySaveV6(now, s.settings.locale);
  return {
    ...base,
    schemaMinor: CURRENT_SCHEMA_MINOR,
    meta: { ...base.meta, savedAt: now, fastMode: s.settings.fastMode, locale: s.settings.locale },
    creature: s.creature ? normalizeCreatureForSave(s.creature) : null,
    npcs: s.npcs,
    lineage: s.lineage,
    discoveredTypes: s.discoveredTypes,
    world: {
      ...s.world,
      position: { x: s.playerX, y: s.playerY },
      wildBestiary: normalizeWildBestiary(s.world.wildBestiary),
    },
    combat: { activeRun: s.activeRun, totalRuns: s.totalRuns },
  };
}

function normalizeCreatureForSave(creature: Creature): NonNullable<SaveV6['creature']> {
  return {
    ...creature,
    lifecycle: {
      ...creature.lifecycle,
      careTraits: creature.lifecycle.careTraits ?? [],
    },
    careProfile: normalizeCareProfile(creature.careProfile),
  };
}

export async function saveSnapshot(snap: SaveV6): Promise<SaveOutcome> {
  const idb = getIdbStore();
  try {
    await idb.set(SAVE_KEY, snap);
    getStore().setSaveMode('idb');
    return { kind: 'persisted', store: 'idb' };
  } catch (err) {
    logger.warn('save.idb-failed', { err: String(err) });
    const local = getLocalStore();
    const encoded = saveCodec.encode(snap);
    if (encoded.length < FALLBACK_MAX_BYTES) {
      try {
        local.set(FALLBACK_KEY, encoded);
        getStore().setSaveMode('localStorage');
        notify({ key: 'save.fallback', severity: 'warn' });
        return { kind: 'persisted', store: 'localStorage' };
      } catch (err2) {
        logger.error('save.localstorage-failed', err2 as Error);
      }
    }
    getStore().setSaveMode('session');
    notify({ key: 'save.session-only', severity: 'warn' });
    return { kind: 'session-only' };
  }
}

/** Persiste o estado atual da store. Usado pelo CareTickService autosave debounced. */
export async function saveCurrentSnapshot(): Promise<SaveOutcome> {
  return saveSnapshot(buildSnapshot());
}

export async function loadSnapshot(): Promise<LoadOutcome> {
  const idb = getIdbStore();
  try {
    const raw = await idb.get<SaveV6>(SAVE_KEY);
    if (raw !== null) {
      const decoded = saveCodec.decode(raw);
      if (decoded) return { kind: 'loaded', snapshot: recoverSnapshotPosition(decoded), from: 'idb' };
      logger.warn('save.idb-corrupted');
      return { kind: 'corrupted' };
    }
  } catch (err) {
    logger.warn('save.idb-load-failed', { err: String(err) });
  }
  const local = getLocalStore();
  const fallbackRaw = local.get<string>(FALLBACK_KEY);
  if (fallbackRaw !== null) {
    const decoded = saveCodec.decode(fallbackRaw);
    if (decoded) return { kind: 'loaded', snapshot: recoverSnapshotPosition(decoded), from: 'localStorage' };
    logger.warn('save.localstorage-corrupted');
    return { kind: 'corrupted' };
  }
  return { kind: 'fresh' };
}

export function recoverSnapshotPosition(snapshot: SaveV6): SaveV6 {
  const migrated = migrateLegacyWorldCoordinates(snapshot);
  if (!migrated.world) return migrated;
  const previous = migrated.world.position ?? { x: 360, y: 1600 };
  const footprint = migrated.creature
    ? footprintForPresentation(
        migrated.creature.genome.body,
        displayHeightForStage(migrated.creature.lifecycle.stage),
      )
    : { radiusX: 18, radiusY: 18 };
  const recovery = recoverWorldPosition(previous, regionRegistry, footprint);
  if (recovery.recovered) {
    logger.warn('world.position-recovered', { from: previous, to: recovery.point, reason: recovery.reason });
  }
  return { ...migrated, world: { ...migrated.world, position: recovery.point } };
}

/** Minor 7 doubled the authored world canvas. Spatial save data is migrated once. */
export function migrateLegacyWorldCoordinates(snapshot: SaveV6): SaveV6 {
  if (!snapshot.world || snapshot.schemaMinor >= 7) return snapshot;
  const scalePoint = <T extends { x: number; y: number }>(point: T): T => ({
    ...point,
    x: point.x * 2,
    y: point.y * 2,
  });
  const world = snapshot.world;
  return {
    ...snapshot,
    schemaMinor: 7,
    world: {
      ...world,
      position: world.position ? scalePoint(world.position) : undefined,
      regionItems: {
        meadow: world.regionItems.meadow.map(scalePoint),
        forest: world.regionItems.forest.map(scalePoint),
        cave: world.regionItems.cave.map(scalePoint),
        coast: world.regionItems.coast.map(scalePoint),
      },
      trees: world.trees?.map(scalePoint),
      memorials: world.memorials?.map(scalePoint),
    },
  };
}

export function makeFreshSnapshot(now: number, locale: 'pt-BR' | 'en' = 'pt-BR'): SaveV6 {
  return emptySaveV6(now, locale);
}

export async function clearSnapshot(): Promise<void> {
  try {
    await getIdbStore().delete(SAVE_KEY);
  } catch (err) {
    logger.warn('save.clear-idb-failed', { err: String(err) });
  }
  try {
    getLocalStore().delete(FALLBACK_KEY);
  } catch (err) {
    logger.warn('save.clear-localstorage-failed', { err: String(err) });
  }
  useStore.getState().clearCreature();
}
